import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Download, Search } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { listStores, listEvents, exportEventRegistrations } from "@/lib/api/admin";
import type { Store, StoreEvent, EventRegistrationExport } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/DataTable";
import type { Column } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { Spinner, SpinnerCenter } from "@/components/Spinner";
import { cn } from "@/lib/utils";

// ─── Constants ────────────────────────────────────────────────────────────────

const AURA_ID = "642e5bba-117f-4d14-9261-d1f5a0145bbb";
const AURA_DEFAULT: Store = {
  id: AURA_ID,
  name: "Aura Nightclub",
  category: "nightclub",
  status: "active",
  price_limit_bypass: false,
  events_enabled: false,
  appointments_enabled: false,
  approved_at: "",
  visible: true,
  created_at: "",
  updated_at: "",
};

type DatePreset = "7d" | "14d" | "30d" | "90d" | "custom";
type StatusFilter = "" | "registered" | "checked_in";

type FieldConfig = {
  first_name: boolean;
  last_name: boolean;
  email: boolean;
  event_title: boolean;
  status: boolean;
  registered_at: boolean;
  checked_in_at: boolean;
};

// ─── Utilities ────────────────────────────────────────────────────────────────

function computeDateRange(
  preset: DatePreset,
  customFrom: string,
  customTo: string
): { startsAfter?: string; startsBefore?: string } {
  if (preset === "custom") {
    return {
      startsAfter: customFrom || undefined,
      startsBefore: customTo || undefined,
    };
  }
  const days = { "7d": 7, "14d": 14, "30d": 30, "90d": 90 }[preset];
  const now = new Date();
  const after = new Date(now);
  after.setDate(now.getDate() - days);
  return {
    startsAfter: after.toISOString(),
    startsBefore: now.toISOString(),
  };
}

function escapeCsvField(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function downloadCsv(data: EventRegistrationExport[], fields: FieldConfig, storeName: string) {
  const fieldOrder: (keyof FieldConfig)[] = [
    "first_name",
    "last_name",
    "email",
    "event_title",
    "status",
    "registered_at",
    "checked_in_at",
  ];
  const labels: Record<keyof FieldConfig, string> = {
    first_name: "First Name",
    last_name: "Last Name",
    email: "Email",
    event_title: "Event",
    status: "Status",
    registered_at: "Registered At",
    checked_in_at: "Checked In At",
  };

  const activeFields = fieldOrder.filter((f) => fields[f]);
  const header = activeFields.map((f) => labels[f]).join(",");

  const rows = data.map((row) => {
    return activeFields
      .map((f) => {
        const val = row[f as keyof EventRegistrationExport];
        return escapeCsvField(val != null ? String(val) : "");
      })
      .join(",");
  });

  const csv = [header, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `disca-guestlist-${storeName.replace(/\s+/g, "-").toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function EventsExportPage() {
  const user = useAdminAuth();

  // Store selection
  const [storeSearch, setStoreSearch] = useState("");
  const [selectedStore, setSelectedStore] = useState<Store>(AURA_DEFAULT);
  const [showStoreDropdown, setShowStoreDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filters
  const [preset, setPreset] = useState<DatePreset>("14d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const [status, setStatus] = useState<StatusFilter>("");

  // CSV fields
  const [fields, setFields] = useState<FieldConfig>({
    first_name: true,
    last_name: true,
    email: true,
    event_title: true,
    status: true,
    registered_at: false,
    checked_in_at: false,
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowStoreDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Store autocomplete — server-side name search, keeps previous results while loading
  const { data: storeResults, isFetching: isStoreSearchFetching } = useQuery({
    queryKey: ["stores-search", storeSearch],
    queryFn: () => listStores(user!, { name: storeSearch, limit: 10 }),
    enabled: !!user && storeSearch.length > 0,
    placeholderData: keepPreviousData,
  });

  const filteredStores = storeResults?.data ?? [];

  // Events query
  const { data: events } = useQuery({
    queryKey: ["events", selectedStore.id],
    queryFn: async () => {
      const events: StoreEvent[] = [];
      let offset = 0;
      while (true) {
        const res = await listEvents(user!, selectedStore.id, 100, offset, "asc");
        events.push(...res.data);
        if (!res.has_more) break;
        offset += res.limit;
      }
      return events;
    },
    enabled: !!user,
  });

  // Date range — memoized so the ISO strings don't change on every render
  const { startsAfter, startsBefore } = useMemo(
    () => computeDateRange(preset, customFrom, customTo),
    [preset, customFrom, customTo]
  );

  // Export data query
  const {
    data: exportData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      "export-events",
      selectedStore.id,
      selectedEventId,
      status,
      startsAfter,
      startsBefore,
    ],
    queryFn: () =>
      exportEventRegistrations(user!, {
        store_id: selectedStore.id,
        event_id: selectedEventId || undefined,
        status: (status as "registered" | "checked_in") || undefined,
        starts_after: selectedEventId ? undefined : startsAfter,
        starts_before: selectedEventId ? undefined : startsBefore,
      }),
    enabled: !!user,
  });

  const uniqueEventCount = useMemo(() => {
    if (!exportData) return 0;
    return new Set(exportData.map((r) => r.event_id)).size;
  }, [exportData]);

  const previewData = exportData?.slice(0, 10) ?? [];

  const columns: Column<EventRegistrationExport>[] = [
    { key: "first_name", header: "First Name", render: (r) => r.first_name },
    { key: "last_name", header: "Last Name", render: (r) => r.last_name },
    { key: "email", header: "Email", render: (r) => r.email || "—" },
    { key: "event_title", header: "Event", render: (r) => r.event_title },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
            r.status === "checked_in"
              ? "bg-green-100 text-green-800"
              : "bg-yellow-100 text-yellow-800"
          )}
        >
          {r.status === "checked_in" ? "Checked In" : "Registered"}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Export Data</h1>
        <p className="text-sm text-muted-foreground">Events · Guestlist</p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Store selector */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Store</label>
            <div className="relative max-w-sm" ref={dropdownRef}>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Search stores…"
                  value={storeSearch}
                  onChange={(e) => {
                    setStoreSearch(e.target.value);
                    setShowStoreDropdown(true);
                  }}
                  onFocus={() => setShowStoreDropdown(true)}
                />
              </div>
              {selectedStore && !storeSearch && (
                <p className="mt-1 text-sm text-muted-foreground">
                  Selected:{" "}
                  <span className="font-medium text-foreground">{selectedStore.name}</span>
                </p>
              )}
              {showStoreDropdown && storeSearch.length > 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-md border bg-popover shadow-md overflow-hidden">
                  {isStoreSearchFetching && filteredStores.length === 0 ? (
                    <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                      <Spinner className="size-3.5 border-muted-foreground" />
                      Searching…
                    </div>
                  ) : filteredStores.length === 0 ? (
                    <p className="px-3 py-2 text-sm text-muted-foreground">No stores found.</p>
                  ) : (
                    filteredStores.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="flex w-full items-center px-3 py-2 text-sm hover:bg-muted"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setSelectedStore(s);
                          setStoreSearch("");
                          setShowStoreDropdown(false);
                          setSelectedEventId("");
                        }}
                      >
                        {s.name}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Date range preset */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Date Range</label>
            <div className="flex flex-wrap gap-2">
              {(["7d", "14d", "30d", "90d", "custom"] as DatePreset[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPreset(p)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-sm transition-colors",
                    preset === p
                      ? "border-primary bg-primary text-white"
                      : "border-border bg-background hover:bg-muted"
                  )}
                >
                  {p === "custom" ? "Custom" : `Last ${p.replace("d", " days")}`}
                </button>
              ))}
            </div>
            {preset === "custom" && (
              <div className="flex flex-wrap gap-2 pt-1">
                <Input
                  type="datetime-local"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="w-auto"
                />
                <Input
                  type="datetime-local"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="w-auto"
                />
              </div>
            )}
          </div>

          {/* Event filter */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Event</label>
            <select
              className="flex h-9 w-full max-w-xs rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
            >
              <option value="">All events</option>
              {events?.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title} –{" "}
                  {new Date(ev.starts_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </option>
              ))}
            </select>
          </div>

          {/* Status filter */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Status</label>
            <div className="flex rounded-md border border-input overflow-hidden w-fit">
              {(
                [
                  { value: "", label: "All" },
                  { value: "registered", label: "Registered" },
                  { value: "checked_in", label: "Checked In" },
                ] as { value: StatusFilter; label: string }[]
              ).map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setStatus(value)}
                  className={cn(
                    "px-3 py-1.5 text-sm transition-colors",
                    status === value ? "bg-primary text-white" : "bg-background hover:bg-muted"
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Fields */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Fields in CSV</label>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  { key: "first_name", label: "First Name" },
                  { key: "last_name", label: "Last Name" },
                  { key: "email", label: "Email" },
                  { key: "event_title", label: "Event" },
                  { key: "status", label: "Status" },
                  { key: "registered_at", label: "Registered At" },
                  { key: "checked_in_at", label: "Checked In At" },
                ] as { key: keyof FieldConfig; label: string }[]
              ).map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFields((f) => ({ ...f, [key]: !f[key] }))}
                  className={cn(
                    "rounded-full border px-3 py-1 text-sm transition-colors",
                    fields[key]
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-background text-muted-foreground hover:bg-muted"
                  )}
                >
                  {fields[key] ? "✓ " : ""}
                  {label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {(isLoading || isFetching) && !exportData && <SpinnerCenter />}

      {exportData && exportData.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{exportData.length}</span> registration
              {exportData.length !== 1 ? "s" : ""} across{" "}
              <span className="font-medium text-foreground">{uniqueEventCount}</span> event
              {uniqueEventCount !== 1 ? "s" : ""}
            </p>
            <Button
              onClick={() => downloadCsv(exportData, fields, selectedStore.name)}
              disabled={exportData.length === 0}
            >
              <Download className="size-4 mr-2" />
              Export CSV ({exportData.length})
            </Button>
          </div>

          <DataTable columns={columns} data={previewData} rowKey={(r) => r.registration_id} />

          {exportData.length > 10 && (
            <p className="text-sm text-muted-foreground text-center">
              +{exportData.length - 10} more rows in the exported file
            </p>
          )}
        </div>
      )}

      {exportData && exportData.length === 0 && (
        <EmptyState
          title="No registrations found"
          description="Try adjusting your filters or selecting a different date range."
        />
      )}
    </div>
  );
}
